/**
 * 広告画像一覧カード用のスライドプレビュー（読み取り専用、Phase 41 改）
 *
 * - InteractiveCanvas と同じ DOM 構造で要素をレンダリングする
 * - ただし選択枠・ハンドル・スナップ・ドラッグなどの編集 UI は持たない
 * - container 幅を受け取り、preset サイズに対して transform: scale で縮小
 *
 * このアプローチを取る理由:
 *   html-to-image でサーバ保存型のサムネイルを試みたが、Google Fonts 全 27 種類の
 *   インライン化処理で 30 秒+のハングが発生した（filter / skipFonts / fontEmbedCSS
 *   いずれも効果なし）。リスト側で直接 DOM をレンダリングするほうが軽量で確実。
 */
import type { AdElement, AdImageSlide, AdPathElement, AdSizePreset } from '@goenhub/shared';
import { findAdVectorShape } from '@goenhub/shared';

interface Props {
  slide: AdImageSlide;
  preset: AdSizePreset;
  /** 表示する幅 (px)。高さは preset のアスペクト比から自動算出 */
  displayWidth: number;
}

export function SlideThumbnail({ slide, preset, displayWidth }: Props) {
  const scale = displayWidth / preset.width;
  const displayHeight = preset.height * scale;

  // 表示順: layerOrder の先頭が最前面なので reverse して背面から描画
  const order = slide.layerOrder?.length
    ? slide.layerOrder
    : slide.elements.map((e) => e.id);
  const elementMap = new Map(slide.elements.map((e) => [e.id, e]));
  const renderOrder = [...order]
    .reverse()
    .map((id) => elementMap.get(id))
    .filter((e): e is AdElement => Boolean(e));

  // 背景画像のレイアウト計算
  const bg = slide.background;
  const bgX = bg?.x ?? 0;
  const bgY = bg?.y ?? 0;
  const bgW = bg?.w ?? preset.width;
  const bgH = bg?.h ?? preset.height;
  const bgFit = bg?.fit ?? 'cover';
  const bgOpacity = bg?.opacity ?? 1;

  return (
    <div
      style={{
        width: displayWidth,
        height: displayHeight,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <div
        style={{
          width: preset.width,
          height: preset.height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          inset: 0,
        }}
      >
        {/* 背景 */}
        {bg?.url && (
          <div
            style={{
              position: 'absolute',
              left: bgX,
              top: bgY,
              width: bgW,
              height: bgH,
              opacity: bgOpacity,
              pointerEvents: 'none',
            }}
          >
            <img
              src={bg.url}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: bgFit,
                display: 'block',
                userSelect: 'none',
              }}
            />
          </div>
        )}

        {/* 要素 */}
        {renderOrder.map((el) => {
          if (!el.visible) return null;
          const transforms: string[] = [];
          if (el.rotation) transforms.push(`rotate(${el.rotation}deg)`);
          if (el.flipX) transforms.push('scaleX(-1)');
          if (el.flipY) transforms.push('scaleY(-1)');
          const baseStyle: React.CSSProperties = {
            position: 'absolute',
            left: el.x,
            top: el.y,
            width: el.w,
            height: el.h,
            transform: transforms.length ? transforms.join(' ') : undefined,
            pointerEvents: 'none',
          };

          if (el.kind === 'text') {
            return (
              <div
                key={el.id}
                style={{
                  ...baseStyle,
                  fontFamily: el.fontFamily,
                  fontSize: el.fontSize,
                  fontWeight: el.fontWeight,
                  color: el.color,
                  textAlign: el.align,
                  lineHeight: el.lineHeight ?? 1.2,
                  letterSpacing: el.letterSpacing ?? 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {el.content}
              </div>
            );
          }

          if (el.kind === 'image_placeholder') {
            if (!el.imageUrl) {
              return (
                <div
                  key={el.id}
                  style={{
                    ...baseStyle,
                    background:
                      'repeating-linear-gradient(45deg, #eee 0 8px, #f5f5f5 8px 16px)',
                    borderRadius: el.borderRadius ?? 0,
                  }}
                />
              );
            }
            return (
              <img
                key={el.id}
                src={el.imageUrl}
                alt={el.label ?? ''}
                draggable={false}
                style={{
                  ...baseStyle,
                  objectFit: el.fit ?? 'cover',
                  borderRadius: el.borderRadius ?? 0,
                  userSelect: 'none',
                }}
              />
            );
          }

          if (el.kind === 'vector') {
            return <VectorMini key={el.id} element={el} style={baseStyle} />;
          }

          if (el.kind === 'path') {
            return <PathMini key={el.id} element={el} style={baseStyle} />;
          }

          return null;
        })}
      </div>
    </div>
  );
}

function VectorMini({
  element,
  style,
}: {
  element: Extract<AdElement, { kind: 'vector' }>;
  style: React.CSSProperties;
}) {
  // カスタム SVG
  if (element.customSvgUrl) {
    return (
      <img
        src={element.customSvgUrl}
        alt=""
        draggable={false}
        style={{
          ...style,
          objectFit: 'contain',
          opacity: element.opacity ?? 1,
          userSelect: 'none',
        }}
      />
    );
  }
  // rect_rounded / rect は div で描画
  if (element.shape === 'rect_rounded') {
    const defaultR = Math.round(Math.min(element.w, element.h) / 4);
    const r = element.cornerRadius ?? defaultR;
    return (
      <div
        style={{
          ...style,
          borderRadius: `${r}px`,
          backgroundColor: element.fill === 'none' ? 'transparent' : element.fill,
          border: element.stroke === 'none'
            ? 'none'
            : `${element.strokeWidth}px solid ${element.stroke}`,
          opacity: element.opacity ?? 1,
        }}
      />
    );
  }
  if (element.shape === 'rect') {
    return (
      <div
        style={{
          ...style,
          backgroundColor: element.fill === 'none' ? 'transparent' : element.fill,
          border: element.stroke === 'none'
            ? 'none'
            : `${element.strokeWidth}px solid ${element.stroke}`,
          opacity: element.opacity ?? 1,
        }}
      />
    );
  }
  // SVG ベース
  const shape = findAdVectorShape(element.shape);
  if (!shape) return null;
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ ...style, opacity: element.opacity ?? 1, overflow: 'visible' }}
    >
      <path
        d={shape.d}
        fill={element.fill === 'none' ? 'none' : shape.strokeOnly ? 'none' : element.fill}
        stroke={
          shape.strokeOnly
            ? element.fill === 'none'
              ? element.stroke
              : element.fill
            : element.stroke === 'none'
            ? 'none'
            : element.stroke
        }
        strokeWidth={element.strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function PathMini({
  element,
  style,
}: {
  element: AdPathElement;
  style: React.CSSProperties;
}) {
  const points = element.points;
  let d = '';
  if (points.length > 0) {
    d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const cur = points[i];
      d += ` C ${prev.x + prev.cpOutX} ${prev.y + prev.cpOutY}, ${cur.x + cur.cpInX} ${cur.y + cur.cpInY}, ${cur.x} ${cur.y}`;
    }
    if (element.closed) d += ' Z';
  }
  return (
    <svg
      viewBox={`0 0 ${element.w} ${element.h}`}
      preserveAspectRatio="none"
      style={{ ...style, overflow: 'visible' }}
    >
      <path
        d={d}
        fill={element.fill === 'none' ? 'none' : element.fill}
        stroke={element.stroke === 'none' ? 'none' : element.stroke}
        strokeWidth={element.strokeWidth}
        opacity={element.opacity ?? 1}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
